import { IconChevDown } from '@ui/icons'
import Pill from '@ui/Pill'
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

type GroupContext = {
  /** Cards inside a labelled group demote to h3 so the outline stays true. */
  headingLevel: 'h2' | 'h3'
  /** The group owns the lock message; member cards suppress their own. */
  groupLocked: boolean
}

const SectionGroupContext = createContext<GroupContext>({
  headingLevel: 'h2',
  groupLocked: false,
})

export const useSectionGroup = (): GroupContext =>
  useContext(SectionGroupContext)

/**
 * A labelled band of settings sections (design.md §6/§8). Groups turn a
 * flat card list into the organizer's own categories — and they own the
 * lock: one pill on the heading instead of the same pill repeated on
 * every member card, plus a hint naming the deadline before it bites.
 *
 * `collapsibleWhenLocked` folds a fully-locked group down to its heading.
 * Everything that locks at golf-final was decided before the round, so
 * from the poker-night sitting onward the page collapses to what's still
 * actionable. State is derived from the lock, never persisted — a stale
 * remembered "collapsed" would hide live settings.
 *
 * `collapsible` (+ `defaultCollapsed`) makes a group foldable regardless
 * of any lock — the summary's Kvalifisering band uses it to tuck the golf
 * result away as reference once poker is under way, open on demand.
 *
 * Omit `titleKey` for the opening group: a heading above the first card
 * labels nothing (there's no previous topic to change from) and costs
 * vertical space at the top of a mobile page.
 */
const SectionGroup = ({
  titleKey,
  lockHintKey,
  locked = false,
  lockedReasonKey,
  collapsibleWhenLocked = false,
  collapsible: collapsibleProp = false,
  defaultCollapsed,
  children,
}: {
  titleKey?: string
  lockHintKey?: string
  locked?: boolean
  lockedReasonKey?: string
  collapsibleWhenLocked?: boolean
  collapsible?: boolean
  defaultCollapsed?: boolean
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const collapsible = collapsibleProp || (locked && collapsibleWhenLocked)
  // Locked-collapse starts folded; a plain collapsible group honours
  // `defaultCollapsed`. Derived, never persisted.
  const [expanded, setExpanded] = useState(
    !(defaultCollapsed ?? (locked && collapsibleWhenLocked))
  )
  const context = useMemo(
    () => ({ headingLevel: 'h3' as const, groupLocked: locked }),
    [locked]
  )

  if (!titleKey) return children
  const showChildren = !collapsible || expanded
  const contentId = `section-group-${titleKey.replace(/\W/g, '-')}`

  const heading = (
    <span className="flex-1 min-w-0 text-left text-xs font-bold uppercase tracking-caps text-ink-500">
      {t(titleKey)}
    </span>
  )
  const lockPill = locked ? (
    <Pill tone="neutral">{t(lockedReasonKey ?? 'events.settings.locked')}</Pill>
  ) : null

  return (
    <SectionGroupContext.Provider value={context}>
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 pt-2 px-1">
          {collapsible ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={contentId}
              className="flex items-center gap-2 min-h-[44px] -my-2"
            >
              {heading}
              {lockPill}
              <IconChevDown
                size={16}
                className={`text-ink-400 transition-transform flex-shrink-0 ${
                  expanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              {heading}
              {lockPill}
            </div>
          )}
          {!locked && lockHintKey && (
            <p className="text-xs text-ink-500">{t(lockHintKey)}</p>
          )}
        </div>
        {showChildren && (
          <div id={contentId} className="flex flex-col gap-4">
            {children}
          </div>
        )}
      </section>
    </SectionGroupContext.Provider>
  )
}

export default SectionGroup
